import { Spin } from "antd";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthRoute } from "src/App";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import CreateRole from "src/apps/super-admin/tabs/roles/create/CreateRole";
import RolesList from "src/apps/super-admin/tabs/roles/list/RolesList";
import CreateStore from "src/apps/super-admin/tabs/stores/create/CreateStore";
import Settings from "src/apps/vendor/Settings";
import HeaderLayout from "src/components/layouts/Header";
import { getTokenFromLocalStorage } from "src/helpers/helpers";
import { getUserStoreRequest } from "src/redux/actions/app";
import {
  checkViewAccess,
  getActiveApp,
  getActiveStoreId,
  getActiveView,
  getCurrentUserInfo,
  getUserPlanData,
  getUserStoresData,
  isAppInitialized,
  isAppInitializing,
} from "src/redux/selectors/app";
import ForgotPassword from "src/tabs/auth/forgot-password/ForgotPassword";
import Login from "src/tabs/auth/Login";
import Register from "src/tabs/auth/register/Register";
import ResetPassword from "src/tabs/auth/reset-password/ResetPassword";
import VerifyEmail from "src/tabs/auth/verify-email/VerifyEmail";
import Dashboard from "src/tabs/dashboard/Dashboard";
import { ACTIVE_APP } from "src/utils/constants";
import CreateAppointment from "../tabs/appointments/create/CreateAppointment";
import AppointmentsList from "../tabs/appointments/list/AppointmentsList";
import CreateBill from "../tabs/billing/create/CreateBill";
import BillsList from "../tabs/billing/list/BillsList";
import CreateClient from "../tabs/client-management/create/CreateClient";
import ClientsList from "../tabs/client-management/list/ClientsList";
import CreateCoupon from "../tabs/coupons/create/CreateCoupon";
import CouponsList from "../tabs/coupons/list/CouponsList";
import CreateCourse from "../tabs/courses/create/CreateCourse";
import CoursesList from "../tabs/courses/list/CoursesList";
import ExportData from "../tabs/data-export/DataExport";
import Customer from "../tabs/data-export/tabs/Customer";
import CreateEnquiry from "../tabs/enquiry-management/create/CreateEnquiry";
import EnquiryList from "../tabs/enquiry-management/list/EnquiryList";
import CreateExpenses from "../tabs/expenses/create/CreateExpense";
import ExpensesList from "../tabs/expenses/list/ExpensesList";
import InventoryList from "../tabs/inventory-management/list/InventoryList";
import InventoryRequestList from "../tabs/inventory-request/list/InventoryRequestList";
import CreateMembership from "../tabs/memberships/create/CreateMembership";
import MembershipList from "../tabs/memberships/list/MembershipList";
import CreatePackage from "../tabs/packages/create/CreatePackage";
import PackagesList from "../tabs/packages/list/PackagesList";
import ProcurementsList from "../tabs/procurement-management/list/ProcurementsList";
import CreateProduct from "../tabs/products/create/CreateProduct";
import ProductsList from "../tabs/products/list/ProductsList";
import EditPassword from "../tabs/profile/edit/EditPassword";
import EditProfile from "../tabs/profile/edit/EditProfile";
import Profile from "../tabs/profile/Profile";
import CreatePurchaseOrders from "../tabs/purchase-order/create/CreatePurchaseOrders";
import CreatePurchaseOrdersForVendor from "../tabs/purchase-order/create/vendor/CreatePurchaseOrders";
import PurchaseOrderList from "../tabs/purchase-order/list/PurchaseOrderList";
import SelectApp from "../tabs/select-app/SelectApp";
import SelectPlan from "../tabs/select-plan/SelectPlan";
import CreateService from "../tabs/services/create/CreateService";
import ServicesList from "../tabs/services/list/ServicesList";
import CreateStaff from "../tabs/staff-management/create/CreateStaff";
import StaffList from "../tabs/staff-management/list/StaffList";
import EditSettings from "../tabs/store-settings/create/EditSettings";
import CreateVendor from "../tabs/vendor-management/create/CreateVendor";
import VendorsList from "../tabs/vendor-management/list/VendorsList";
import AllInventoryList from "../tabs/vendor-view/inventory-management/all/list/InventoriesList";
import StoreInventoryList from "../tabs/vendor-view/inventory-management/store/list/InventoriesList";
import CreateWithdraw from "../tabs/withdraw/create/CreateWithdraw";
import WithdrawList from "../tabs/withdraw/list/WithdrawList";

function UserRoutes() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector(getCurrentUserInfo);
  const userRole = userInfo?.role ?? 3;

  const activeApp = useSelector(getActiveApp);
  const activeView = useSelector(getActiveView);
  const activeStoreId = useSelector(getActiveStoreId);

  const isLoggedIn = !!getTokenFromLocalStorage();

  const isInitializing = useSelector(isAppInitializing);
  const isInitialized = useSelector(isAppInitialized);

  const hasViewAccessToTab = useSelector((state) => checkViewAccess(state));

  const userStores = useSelector(getUserStoresData);
  const hasStores = useMemo(() => userStores.length, [userStores.length]);

  const hasPlan = useSelector(getUserPlanData);

  const handleInitialRoute = useMemo(() => {
    if (!isLoggedIn) {
      return "/login";
    }
    if (hasPlan === null) {
      return "/select-plan";
    }
    if (!activeApp) {
      return "/select-app";
    }
    if (activeApp === ACTIVE_APP.STORE) {
      if (!hasStores && hasPlan === null) {
        return "/select-plan";
      } else if (!hasStores) {
        return "/store/create-store";
      } else if (hasStores && activeView === "vendor") {
        return "/store/vendor/inventory/all";
      } else if (!location.pathname.includes("store")) {
        return "/store/dashboard";
      }
      return location.pathname;
    } else if (activeApp === ACTIVE_APP.VENDOR) {
      if (!location.pathname.includes("vendor")) {
        return "/vendor/dashboard";
      }
    }
    return location.pathname;
  }, [
    activeApp,
    activeView,
    hasPlan,
    hasStores,
    isLoggedIn,
    location.pathname,
  ]);

  useEffect(() => {
    if (isInitialized) {
      navigate(handleInitialRoute);
    }
  }, [isInitialized]);

  // useEffect(() => {
  //   // send to select-app when user login but not for super-admin
  //   if (
  //     isLoggedIn &&
  //     !activeApp &&
  //     Object.keys(userInfo).length > 0 &&
  //     userInfo?.role !== ROLES.SUPER_ADMIN
  //   ) {
  //     navigate("/select-app");
  //   }
  // }, [activeApp, isLoggedIn, navigate, userInfo]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email/:token?" element={<VerifyEmail />} />
      <Route path="/reset-password/:token?" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route element={<AuthRoute isInLayout={false} />}>
        <Route
          path="/select-app"
          element={
            <HeaderLayout>
              <SelectApp />
            </HeaderLayout>
          }
        />
        <Route
          path="/create-store"
          element={
            <HeaderLayout>
              <CreateStore
                onCancel={() => navigate("/store/dashboard")}
                onSuccess={() => navigate("/store/dashboard")}
              />
            </HeaderLayout>
          }
        />
      </Route>

      <Route path="/" element={<AuthRoute />}>
        <Route
          index
          element={
            !isInitialized ? (
              <div className="flex justify-center items-center h-full">
                <Spin size="large" />
              </div>
            ) : (
              <Navigate to={handleInitialRoute} />
            )
          }
        />
        <Route path="select-plan" element={<SelectPlan />} />
        <Route path="select-app" element={<SelectApp />} />
        <Route path="profile" element={<EditProfile />} />
        <Route path="store">
          <Route index element={<Navigate to="dashboard" />} />
          {hasViewAccessToTab(TAB_NAMES.DASHBOARD) && (
            <Route path="dashboard" element={<Dashboard />} />
          )}
          <Route
            path="create-store"
            element={
              <CreateStore
                onSuccess={() => {
                  dispatch(getUserStoreRequest());
                  navigate("/store/dashboard");
                }}
              />
            }
          />

          <Route path="store" element={<CreateStore />} />

          {/* Appointment routes */}
          {hasViewAccessToTab(TAB_NAMES.APPOINTMENT) && (
            <>
              <Route path="appointments" element={<AppointmentsList />} />
              <Route
                path="appointments/create/:appointmentId?"
                element={<CreateAppointment />}
              />
              <Route
                path="appointments/view/:appointmentId?"
                element={<CreateAppointment isInViewMode={true} />}
              />
            </>
          )}

          {/* Client management routes */}
          {hasViewAccessToTab(TAB_NAMES.CLIENT_MANAGEMENT) && (
            <>
              <Route path="clients" element={<ClientsList />} />
              <Route
                path="clients/create/:clientId?"
                element={<CreateClient />}
              />
              <Route
                path="clients/view/:clientId?"
                element={<CreateClient isInViewMode={true} />}
              />
            </>
          )}

          {/* Staff management routes */}
          {hasViewAccessToTab(TAB_NAMES.STAFF_MANAGEMENT) && (
            <>
              <Route path="staff" element={<StaffList />} />
              <Route path="staff/create/:staffId?" element={<CreateStaff />} />
              <Route
                path="staff/view/:staffId?"
                element={<CreateStaff isInViewMode={true} />}
              />
            </>
          )}

          {/* Service management routes */}
          {hasViewAccessToTab(TAB_NAMES.SERVICE_MANAGEMENT) && (
            <>
              <Route path="services" element={<ServicesList />} />
              <Route
                path="services/create/:serviceId?"
                element={<CreateService />}
              />
              <Route
                path="services/view/:serviceId?"
                element={<CreateService isInViewMode={true} />}
              />
            </>
          )}

          {/* Package management routes */}
          {hasViewAccessToTab(TAB_NAMES.PACKAGE_MANAGEMENT) && (
            <>
              <Route path="packages" element={<PackagesList />} />
              <Route
                path="packages/create/:packageId?"
                element={<CreatePackage />}
              />
              <Route
                path="packages/view/:packageId?"
                element={<CreatePackage isInViewMode={true} />}
              />
            </>
          )}

          {/* Course management routes */}
          {hasViewAccessToTab(TAB_NAMES.COURSE_MANAGEMENT) && (
            <>
              <Route path="courses" element={<CoursesList />} />
              <Route
                path="courses/create/:courseId?"
                element={<CreateCourse />}
              />
              <Route
                path="courses/view/:courseId?"
                element={<CreateCourse isInViewMode={true} />}
              />
            </>
          )}

          {/* Inventory management routes */}
          {hasViewAccessToTab(TAB_NAMES.INVENTORY_MANAGEMENT) && (
            <Route path="inventory" element={<InventoryList />} />
          )}

          {/* Inventory request routes */}
          {hasViewAccessToTab(TAB_NAMES.INVENTORY_REQUEST) && (
            <Route
              path="inventory/request"
              element={<InventoryRequestList />}
            />
          )}

          {/* Membership management routes */}
          {hasViewAccessToTab(TAB_NAMES.MEMBERSHIP_MANAGEMENT) && (
            <>
              <Route path="memberships" element={<MembershipList />} />
              <Route
                path="memberships/create/:membershipId?"
                element={<CreateMembership />}
              />
              <Route
                path="memberships/view/:membershipId?"
                element={<CreateMembership isInViewMode={true} />}
              />
            </>
          )}

          {/* Coupon management routes */}
          {hasViewAccessToTab(TAB_NAMES.COUPON_MANAGEMENT) && (
            <>
              <Route path="coupons" element={<CouponsList />} />
              <Route
                path="coupons/create/:couponId?"
                element={<CreateCoupon />}
              />
              <Route
                path="coupons/view/:couponId?"
                element={<CreateCoupon isInViewMode={true} />}
              />
            </>
          )}

          {/* Enquiry management routes */}
          {hasViewAccessToTab(TAB_NAMES.ENQUIRY_MANAGEMENT) && (
            <>
              <Route path="enquiries" element={<EnquiryList />} />
              <Route
                path="enquiries/create/:enquiryId?"
                element={<CreateEnquiry />}
              />
              <Route
                path="enquiries/view/:enquiryId?"
                element={<CreateEnquiry isInViewMode={true} />}
              />
            </>
          )}

          {/* Enquiry management routes */}
          {hasViewAccessToTab(TAB_NAMES.BILLING) && (
            <>
              <Route path="billing" element={<BillsList />} />
              <Route path="billing/create/:billId?" element={<CreateBill />} />
              <Route
                path="billing/view/:billId?"
                element={<CreateBill isInViewMode={true} />}
              />
            </>
          )}

          {/* Withdraw management routes */}
          {hasViewAccessToTab(TAB_NAMES.WITHDRAW) && (
            <>
              <Route path="withdraw" element={<WithdrawList />} />
              <Route
                path="withdraw/create/:withdrawId?"
                element={<CreateWithdraw />}
              />
              <Route
                path="withdraw/view/:withdrawId?"
                element={<CreateWithdraw isInViewMode={true} />}
              />
            </>
          )}

          {/* Expenses management routes */}
          {hasViewAccessToTab(TAB_NAMES.EXPENSES) && (
            <>
              <Route path="expenses" element={<ExpensesList />} />
              <Route
                path="expenses/create/:expenseId?"
                element={<CreateExpenses />}
              />
              <Route
                path="expenses/view/:expenseId?"
                element={<CreateExpenses isInViewMode={true} />}
              />
            </>
          )}

          {/* Roles route */}
          {hasViewAccessToTab(TAB_NAMES.ROLES) && (
            <>
              <Route path="roles" element={<RolesList />} />
              <Route
                path="roles/create/:roleId?"
                element={
                  <CreateRole onSuccess={() => navigate("/store/roles")} />
                }
              />
              <Route
                path="roles/view/:roleId?"
                element={<CreateRole isInViewMode={true} />}
              />
            </>
          )}

          <Route path="data-export" element={<ExportData />}>
            <Route index element={<Navigate to="customer" />} />
            <Route path="customer" element={<Customer />} />
            <Route path="order" element={<>Order</>} />
            <Route path="enquiry" element={<>Enquiry</>} />
          </Route>

          {/* Vendor routes for admin */}
          <Route path="vendor">
            {hasViewAccessToTab(TAB_NAMES.ALL_INVENTORY) && (
              <>
                <Route path="inventory/all" element={<AllInventoryList />} />
                <Route
                  path="inventory/all/create/:productId?"
                  element={
                    <CreateProduct
                      label="Add New Products"
                      tabName={TAB_NAMES.ALL_INVENTORY}
                      onSuccess={() => navigate("/store/vendor/inventory/all")}
                    />
                  }
                />
                <Route
                  path="inventory/all/view/:productId?"
                  element={
                    <CreateProduct
                      label="Add New Products"
                      tabName={TAB_NAMES.ALL_INVENTORY}
                      onSuccess={() => navigate("/store/vendor/inventory/all")}
                    />
                  }
                />
              </>
            )}
            {hasViewAccessToTab(TAB_NAMES.STORE_INVENTORY) && (
              <Route path="inventory/store" element={<StoreInventoryList />} />
            )}
            {/* {hasViewAccessToTab(TAB_NAMES.INVENTORY_REQUEST) && (
              <Route
                path="inventory/request"
                element={<InventoryRequestList />}
              />
            )} */}
            {hasViewAccessToTab(TAB_NAMES.PROCUREMENT_REQUEST) && (
              <>
                <Route path="procurements" element={<ProcurementsList />} />
                <Route
                  path="procurements/create/:purchaseOrderId?"
                  element={
                    <CreatePurchaseOrders
                      label="Procurement Request"
                      activeTab={TAB_NAMES.PROCUREMENT_REQUEST}
                    />
                  }
                />
              </>
            )}
            {hasViewAccessToTab(TAB_NAMES.VENDOR_MANAGEMENT) && (
              <>
                <Route path="vendors" element={<VendorsList />} />
                <Route
                  path="vendors/create/:vendorId?"
                  element={<CreateVendor />}
                />
                <Route
                  path="vendors/view/:vendorId?"
                  element={<CreateVendor isInViewMode={true} />}
                />
              </>
            )}

            {hasViewAccessToTab(TAB_NAMES.PURCHASE_ORDER) && (
              <>
                <Route path="purchase-orders" element={<PurchaseOrderList />} />
                <Route
                  path="purchase-orders/create/:purchaseOrderId?"
                  element={
                    <CreatePurchaseOrders
                      label="Purchase Order"
                      activeTab={TAB_NAMES.PURCHASE_ORDER}
                    />
                  }
                />
                <Route
                  path="purchase-orders/view/:purchaseOrderId?"
                  element={
                    <CreatePurchaseOrders
                      label="Purchase Order"
                      activeTab={TAB_NAMES.PURCHASE_ORDER}
                      isInViewMode={true}
                    />
                  }
                />
              </>
            )}
          </Route>
          <Route path="profile" element={<Profile />}>
            <Route index element={<Navigate to="profile" />} />
            <Route path="edit" element={<EditProfile />} />
            <Route path="security" element={<EditPassword />} />
          </Route>
          {hasViewAccessToTab(TAB_NAMES.EDIT_STORE_SETTINGS) && (
            <Route path="edit-settings" element={<EditSettings />} />
          )}
        </Route>
        <Route path="vendor">
          <Route index element={<Navigate to="dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          {hasViewAccessToTab(TAB_NAMES.PRODUCTS) && (
            <>
              <Route path="products" element={<ProductsList />} />
              <Route
                path="products/create/:productId?"
                element={<CreateProduct label="Add New Products" />}
              />
              <Route
                path="products/view/:productId?"
                element={
                  <CreateProduct label="Add New Products" isInViewMode={true} />
                }
              />
            </>
          )}
          {hasViewAccessToTab(TAB_NAMES.PROCUREMENT_REQUEST) && (
            <Route path="procurements" element={<ProcurementsList />} />
          )}
          {hasViewAccessToTab(TAB_NAMES.PURCHASE_ORDER) && (
            <>
              <Route path="purchase-orders" element={<PurchaseOrderList />} />
              <Route
                path="purchase-orders/create/:purchaseOrderId?"
                element={<CreatePurchaseOrders />}
              />
              <Route
                path="purchase-orders/view/:purchaseOrderId?"
                element={<CreatePurchaseOrders isInViewMode={true} />}
              />
              <Route
                path="purchase-orders/vendor/create/:purchaseOrderId?"
                element={<CreatePurchaseOrdersForVendor />}
              />
            </>
          )}
          {hasViewAccessToTab(TAB_NAMES.DASHBOARD) && (
            <Route path="dashboard" element={<div>Vendor Dashboard</div>} />
          )}

          {hasViewAccessToTab(TAB_NAMES.DASHBOARD) && (
            <Route path="products" element={<div>Vendor Products</div>} />
          )}
          <Route path="settings" element={<Settings />} />
        </Route>
      </Route>
      <Route
        path="*"
        element={
          <div className="flex justify-center items-center h-full">404</div>
        }
      />
    </Routes>
  );
}

export default UserRoutes;
